// src/App.js
import React, { Suspense, lazy, useEffect } from 'react';
import { Route, Routes, useLocation, useParams, Navigate } from 'react-router-dom';
import PublicHeader from './components/PublicHeader';
import LandingPage from './components/LandingPage';
import PublicFooter from './components/PublicFooter';
import Book from './components/Book';
import Contact from './components/Contact';
import CookiesPolicy from './components/CookiesPolicy';
import PrivacyPolicy from './components/PrivacyPolicy';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import { AuthProvider } from './context/AuthContext';
import BlogList from './components/blog/BlogList';
import BlogPost from './components/blog/BlogPost';
import PreInspect from './components/services/PreInspection';
import RepairInspection from './components/services/RepairInspection';
import PostRepairVerification from './components/services/PostRepairVerification';
import RoadSimulationTesting from './components/services/RoadSimulationTesting';
import VehicleAppraisalsForBanks from './components/services/VehicleAppraisalsForBanks';
import CarRepairService from './components/services/CarRepair';
import CarInspectionSeoPage from './components/seo/CarInspect';
import TradingTools from './components/seo/TradingTools';
import PublicFloating from './components/PublicFloating';
import AbuDhabiCarInspect from './components/seo/AbuDhabiCarInspect';
import MobileCarInspection from './components/seo/MobileCarInspection';
import ProtectedRoute from './components/ProtectedRoute';
import { getStoredLanguage } from './languagePreference';

// Operational/admin tooling (report builder, PDF export, dashboards) pulls in
// jspdf/pdf-lib/pdfmake/html2canvas/recharts/TinyMCE — load it on demand only,
// so public marketing pages don't ship that weight in the main bundle.
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const AddPost = lazy(() => import('./components/blog/AddPost'));
const EditPost = lazy(() => import('./components/blog/EditPost'));
const ReportList = lazy(() => import('./components/report/ReportList'));
const ViewReport = lazy(() => import('./components/report/ViewReport'));
const CarPaintForm = lazy(() => import('./components/report/CarPaintForm'));
const AddReport = lazy(() => import('./components/report/AddReport'));
const PaintColorFiller = lazy(() => import('./components/report/PaintColorFiller'));
const EditReport = lazy(() => import('./components/report/EditReport'));
const TechDashboard = lazy(() => import('./components/report/TechDashboard'));
const AdminDashboard = lazy(() => import('./components/report/AdminDashboard'));
const SalesSystem = lazy(() => import('./components/report/SalesSystem'));
const CustomerSystem = lazy(() => import('./components/report/CustomerSystem'));
const Techs = lazy(() => import('./components/report/Techs'));
const Maintenance = lazy(() => import('./components/report/Maintenance'));
const MaintenanceRecords = lazy(() => import('./components/report/MaintenanceRecords'));
const EditMaintenance = lazy(() => import('./components/report/EditMaintenance'));
const ViewMaintenance = lazy(() => import('./components/report/ViewMaintenance'));
const Invoice = lazy(() => import('./components/report/Invoice'));
const InvoiceList = lazy(() => import('./components/report/InvoiceList'));
const ViewInvoice = lazy(() => import('./components/report/ViewInvoice'));
const EditInvoice = lazy(() => import('./components/report/EditInvoice'));
const InvoicePDFViewer = lazy(() => import('./components/report/InvoicePDFViewer'));
const Expenses = lazy(() => import('./components/report/Expenses'));
const Total = lazy(() => import('./components/report/Total'));
const AttendanceCheckIn = lazy(() => import('./components/attendance/AttendanceCheckIn'));
const AttendanceAdmin = lazy(() => import('./components/attendance/AttendanceAdmin'));
const StaffLayout = lazy(() => import('./components/staff/StaffLayout'));

const LangRedirect = ({ to }) => {
  const { lang } = useParams();
  return <Navigate to={`/${lang}/${to}`} replace />;
};

const PublicReportPage = () => (
  <div className="min-h-screen bg-gray-100 py-6 px-3">
    <ViewReport publicView />
  </div>
);

const RouteFallback = () => (
  <div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
    <div style={{ width: 34, height: 34, border: '3px solid rgba(2,168,221,0.25)', borderTopColor: '#02a8dd', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
  </div>
);

function App() {
  const location = useLocation();
  const preferredLanguage = getStoredLanguage() || 'ar';
  const isLandingPage = /^\/(ar|en)\/?$/.test(location.pathname);
  const isOperationalRoute = /^\/reports\/view\//.test(location.pathname) || /^\/(ar|en)\/(dashboard|add-post|edit-post|login|register|add-report|reports|report\/|car-paint|new-report|car-diagram|edit-report|tech-dashboard|admin-dashboard|maintenance|maintenance-records|edit-maintenance|view-maintenance|invoice|invoices|invoice-pdf|edit-invoice|sales|customers|techs|expenses|total|attendance)/.test(location.pathname);

  useEffect(() => {
    if (window.gtag) {
      // Track page view for both Google Analytics and Google Ads
      window.gtag('config', 'AW-16721393829', {
        page_path: location.pathname,
      });
      window.gtag('config', 'G-776NRXFJYE', {
        page_path: location.pathname,
      });
    }
  }, [location]);

  return (
    <AuthProvider>
        <div className="app-shell">
          {!isOperationalRoute && <PublicHeader />}
          <main className={isLandingPage || isOperationalRoute ? '' : 'pt-20 sm:pt-24'}>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Redirect to default language (Arabic) if no language is in the URL */}
                <Route path="/" element={<Navigate to={`/${preferredLanguage}/`} replace />} />

                {/* Define routes with :lang parameter for language support */}
                <Route path="/:lang/" element={
                  <LandingPage />
                } />
                <Route path="/:lang/book" element={<Book />} />
                <Route path="/:lang/contact" element={<Contact />} />
                <Route path="/:lang/cookies-policy" element={<CookiesPolicy />} />
                <Route path="/:lang/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/:lang/login" element={<Login />} />
                <Route path="/:lang/register" element={<Register />} />
                <Route path="/:lang/blog" element={<BlogList />} />
                <Route path="/:lang/blog/:id" element={<BlogPost />} />

                {/* Services Routes */}
                <Route path="/:lang/services/pre-purchase-inspections" element={<PreInspect />} />
                <Route path="/:lang/services/pre-repair-diagnostics" element={<RepairInspection />} />
                <Route path="/:lang/services/post-repair-verification" element={<PostRepairVerification />} />
                <Route path="/:lang/services/road-simulation-testing" element={<RoadSimulationTesting />} />
                <Route path="/:lang/services/vehicle-appraisals-for-banks" element={<VehicleAppraisalsForBanks />} />
                <Route path="/:lang/services/car-repair-service" element={<CarRepairService />} />
                <Route path="/:lang/car-inspection-uae" element={<CarInspectionSeoPage />} />
                <Route path="/:lang/trading-tools" element={<TradingTools />} />
                <Route path="/:lang/abu-dhabi-car-inspect" element={<AbuDhabiCarInspect />} />
                <Route exact path="/:lang/mobile-car-inspection" element={<MobileCarInspection />} />
                {/* Public report page: the QR code on printed reports opens
                    /reports/view/<id>. No login; customer contact details are hidden. */}
                <Route path="/reports/view/:id" element={<PublicReportPage />} />
                <Route path="/:lang/reports/view/:id" element={<PublicReportPage />} />

                {/* Old staff sign-in links now use the single login page */}
                <Route path="/:lang/tech-login" element={<LangRedirect to="login" />} />
                <Route path="/:lang/tech-register" element={<LangRedirect to="register" />} />

                {/* Staff pages (admins and techs): top bar with breadcrumbs */}
                <Route element={<ProtectedRoute />}>
                <Route element={<StaffLayout />}>
                  <Route path="/:lang/add-report" element={<AddReport />} />
                  <Route path="/:lang/reports" element={<ReportList />} />
                  <Route path="/:lang/report/:id" element={<ViewReport />} />
                  <Route path="/:lang/car-paint" element={<CarPaintForm />} />
                  <Route path="/:lang/new-report" element={<AddReport />} />
                  <Route path="/:lang/car-diagram" element={<PaintColorFiller />} />
                  <Route path='/:lang/edit-report/:id' element={<EditReport />} />
                  <Route path="/:lang/tech-dashboard" element={<TechDashboard />} />
                  <Route path="/:lang/maintenance" element={<Maintenance />} />
                  <Route path="/:lang/maintenance-records" element={<MaintenanceRecords />} />
                  <Route path="/:lang/edit-maintenance/:id" element={<EditMaintenance />} />
                  <Route path="/:lang/view-maintenance/:id" element={<ViewMaintenance />} />
                  <Route path="/:lang/invoice" element={<Invoice />} />
                  <Route path="/:lang/invoices" element={<InvoiceList />} />
                  <Route path="/:lang/invoice/:id" element={<ViewInvoice />} />
                  <Route path="/:lang/edit-invoice/:id" element={<EditInvoice />} />
                  <Route path="/:lang/invoice-pdf/:id" element={<InvoicePDFViewer />} />
                  <Route path="/:lang/attendance" element={<AttendanceCheckIn />} />

                  {/* Admins only */}
                  <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
                  <Route path="/:lang/admin-dashboard" element={<AdminDashboard />} />
                  <Route path="/:lang/dashboard" element={<Dashboard />} />
                  <Route path="/:lang/add-post" element={<AddPost />} />
                  <Route path="/:lang/edit-post/:id" element={<EditPost />} />
                  <Route path="/:lang/sales" element={<SalesSystem />} />
                  <Route path="/:lang/customers" element={<CustomerSystem />} />
                  <Route path="/:lang/techs" element={<Techs />} />
                  <Route path="/:lang/expenses" element={<Expenses />} />
                  <Route path="/:lang/total" element={<Total />} />
                  <Route path="/:lang/attendance-admin" element={<AttendanceAdmin />} />
                  </Route>
                </Route>
                </Route>
              </Routes>
            </Suspense>
          </main>
          {!isOperationalRoute && <PublicFooter />}
          {!isOperationalRoute && <PublicFloating />}
        </div>
    </AuthProvider>
  );
}

export default App;
